import _get from 'lodash/get';

// Shaka error categories -> human readable description.
// @see https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
const SHAKA_ERROR_DESCRIPTION_BY_CATEGORY = {
  1: 'network',
  2: 'text',
  3: 'media',
  4: 'manifest',
  5: 'streaming',
  6: 'drm',
  7: 'player',
  8: 'cast',
  9: 'storage',
};

// Shaka critical severity that should be reported as a fatal error...
const FATAL_SHAKA_SEVERITY = 2;
// ...except for these codes, which are recoverable in our setup.
const NON_FATAL_PLAYER_ERROR_CODES = [1002, 3016];

/**
 * Derives rich playback-error metadata (code, human readable message, serialized
 * metadata and fatality) from a Shaka/media-engine error.
 *
 * The structured Shaka error detail (code/category/severity/data) is carried on
 * the wrapped media-engine error under `content`; raw Shaka errors are handled too.
 *
 * @param {Object} error Shaka or media-engine error object
 * @returns {{ code: number, message: string, metadata: (Array|undefined), fatal: boolean }}
 */
export const getShakaErrorInfo = (error = {}) => {
  const detail = error.content && typeof error.content === 'object' ? error.content : error;
  const code = _get(detail, 'code', error.code);
  const category = _get(detail, 'category', error.category);
  const severity = _get(detail, 'severity', error.severity);
  const data = _get(detail, 'data');

  let message = _get(detail, 'message') || error.message || error.reason || 'unknown';

  if (category > 0 && category < 10) {
    message = SHAKA_ERROR_DESCRIPTION_BY_CATEGORY[category] || message;
  }

  if (Array.isArray(data) && data.length) {
    if (code === 1001 && data[0] && data[1]) {
      message = `Bad HTTP status ${data[1]} on ${data[0]}`;
    } else if (code === 1002 && data[0]) {
      message = `HTTP error on ${data[0]}`;
    } else if (code === 1003 && data[0]) {
      message = `Request timeout: ${data[0]}`;
    }
  }

  let metadata;
  try {
    // In case if metadata could not be serialized, let's skip it
    metadata = Array.isArray(data) && data.length ? JSON.parse(JSON.stringify(data)) : undefined;
  } catch (serializationError) {
    metadata = undefined;
  }

  const fatal = Number.isFinite(severity)
    ? severity === FATAL_SHAKA_SEVERITY && !NON_FATAL_PLAYER_ERROR_CODES.includes(code)
    : Boolean(error.fatal || _get(detail, 'fatal'));

  return { code, message, metadata, fatal };
};
